(() => {
  'use strict';

  const STORAGE_KEY = 'card-republic-wishlist-v1';
  let memoryFallback = [];
  let toastTimer = null;
  const S = window.cardRepublicStrings?.wishlist || {};
  const locale = window.cardRepublicStrings?.locale || document.documentElement.lang || 'en';
  const isSk = String(locale).toLowerCase().startsWith('sk');
  const fmt = (value, vars = {}) => Object.entries(vars).reduce(
    (out, [key, replacement]) => String(out || '').replaceAll(`[${key}]`, String(replacement)),
    value || '',
  );

  const read = () => {
    try {
      const parsed = JSON.parse(window.localStorage.getItem(STORAGE_KEY) || '[]');
      return Array.isArray(parsed) ? parsed : [];
    } catch (error) {
      return memoryFallback;
    }
  };

  const write = (items) => {
    memoryFallback = items;
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    } catch (error) {
      // Browser storage can be unavailable in strict privacy modes.
    }
  };

  const normalize = (button) => ({
    id: String(button.dataset.productId || ''),
    handle: button.dataset.productHandle || '',
    title: button.dataset.productTitle || '',
    url: button.dataset.productUrl || '',
    image: button.dataset.productImage || '',
    price: button.dataset.productPrice || '',
    priceCents: Number(button.dataset.productPriceCents || 0),
    available: button.dataset.productAvailable === 'true',
    addedAt: new Date().toISOString(),
  });

  const has = (id) => read().some((item) => String(item.id) === String(id));

  const showToast = (message) => {
    let toast = document.querySelector('[data-cr-wishlist-toast]');
    if (!toast) {
      toast = document.createElement('div');
      toast.className = 'cr-wishlist-toast';
      toast.dataset.crWishlistToast = '';
      toast.setAttribute('role', 'status');
      toast.setAttribute('aria-live', 'polite');
      document.body.appendChild(toast);
    }

    toast.textContent = message;
    toast.classList.add('is-visible');
    window.clearTimeout(toastTimer);
    toastTimer = window.setTimeout(() => toast.classList.remove('is-visible'), 2300);
  };

  const syncButtons = () => {
    const items = read();
    document.querySelectorAll('[data-cr-wishlist-toggle]').forEach((button) => {
      const active = items.some((item) => String(item.id) === String(button.dataset.productId));
      button.classList.toggle('is-active', active);
      button.setAttribute('aria-pressed', active ? 'true' : 'false');
      button.setAttribute('title', active ? (S.remove || 'Remove from wishlist') : (S.add || 'Add to wishlist'));
      button.setAttribute(
        'aria-label',
        active
          ? fmt(S.removeProduct || 'Remove [product] from wishlist', { product: button.dataset.productTitle || S.productFallback || 'product' })
          : fmt(S.addProduct || 'Add [product] to wishlist', { product: button.dataset.productTitle || S.productFallback || 'product' }),
      );

      const hidden = button.querySelector('.visually-hidden');
      if (hidden) hidden.textContent = active ? (S.remove || 'Remove from wishlist') : (S.add || 'Add to wishlist');
    });

    document.querySelectorAll('[data-cr-wishlist-count]').forEach((element) => {
      element.textContent = String(items.length);
    });
  };

  const toggle = (button) => {
    const product = normalize(button);
    if (!product.id) return;

    const items = read();
    const index = items.findIndex((item) => String(item.id) === product.id);

    if (index >= 0) {
      items.splice(index, 1);
      write(items);
      showToast(S.removed || 'Product removed from wishlist.');
    } else {
      items.unshift(product);
      write(items);
      showToast(S.added || 'Product added to wishlist.');
    }

    syncButtons();
    renderWishlistPages();
    window.dispatchEvent(new CustomEvent('cr:wishlist:change', { detail: { items } }));
  };

  const createSvg = (type) => {
    const ns = 'http://www.w3.org/2000/svg';
    const svg = document.createElementNS(ns, 'svg');
    svg.setAttribute('viewBox', '0 0 24 24');
    svg.setAttribute('aria-hidden', 'true');

    if (type === 'trash') {
      const path1 = document.createElementNS(ns, 'path');
      path1.setAttribute('d', 'M3 6h18');
      const path2 = document.createElementNS(ns, 'path');
      path2.setAttribute('d', 'M8 6V4h8v2');
      const path3 = document.createElementNS(ns, 'path');
      path3.setAttribute('d', 'm19 6-1 14H6L5 6');
      svg.append(path1, path2, path3);
    } else {
      const path = document.createElementNS(ns, 'path');
      path.setAttribute('d', 'M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78L12 21.23l8.84-8.84a5.5 5.5 0 0 0 0-7.78Z');
      svg.append(path);
    }

    return svg;
  };

  const createWishlistCard = (item) => {
    const article = document.createElement('article');
    article.className = 'cr-wishlist-card';
    article.dataset.productId = item.id;

    const mediaLink = document.createElement('a');
    mediaLink.className = 'cr-wishlist-card__media';
    mediaLink.href = item.url || `/products/${item.handle}`;
    mediaLink.setAttribute('aria-label', item.title || S.viewProduct || (isSk ? 'Pozrieť produkt' : 'View product'));

    if (item.image) {
      const image = document.createElement('img');
      image.src = item.image;
      image.alt = item.title || '';
      image.loading = 'lazy';
      image.decoding = 'async';
      mediaLink.appendChild(image);
    }

    const body = document.createElement('div');
    body.className = 'cr-wishlist-card__body';

    const title = document.createElement('h2');
    title.className = 'cr-wishlist-card__title';
    title.textContent = item.title || S.productTitleFallback || 'Product';

    const price = document.createElement('p');
    price.className = 'cr-wishlist-card__price';
    price.textContent = item.price || '';

    const status = document.createElement('p');
    status.className = `cr-wishlist-card__status${item.available ? '' : ' is-sold-out'}`;
    status.textContent = item.available ? (S.inStock || (isSk ? 'Dostupné' : 'In stock')) : (S.soldOut || (isSk ? 'Momentálne vypredané' : 'Currently sold out'));

    const actions = document.createElement('div');
    actions.className = 'cr-wishlist-card__actions';

    const view = document.createElement('a');
    view.className = 'cr-wishlist-card__view';
    view.href = item.url || `/products/${item.handle}`;
    view.textContent = S.viewProduct || (isSk ? 'Pozrieť produkt' : 'View product');

    const remove = document.createElement('button');
    remove.type = 'button';
    remove.className = 'cr-wishlist-card__remove';
    remove.dataset.crWishlistRemove = item.id;
    remove.setAttribute('aria-label', fmt(S.removeProduct || 'Remove [product] from wishlist', { product: item.title || S.productFallback || 'product' }));
    remove.title = S.remove || 'Remove from wishlist';
    remove.appendChild(createSvg('trash'));

    actions.append(view, remove);
    body.append(title, price, status, actions);
    article.append(mediaLink, body);
    return article;
  };

  const renderWishlistPages = () => {
    const items = read();

    document.querySelectorAll('[data-cr-wishlist-page]').forEach((page) => {
      const grid = page.querySelector('[data-cr-wishlist-grid]');
      const empty = page.querySelector('[data-cr-wishlist-empty]');
      if (!grid || !empty) return;

      grid.replaceChildren();
      items.forEach((item) => grid.appendChild(createWishlistCard(item)));
      grid.hidden = items.length === 0;
      empty.hidden = items.length !== 0;
    });

    syncButtons();
  };

  const removeById = (id) => {
    const items = read().filter((item) => String(item.id) !== String(id));
    write(items);
    showToast(S.removed || 'Product removed from wishlist.');
    renderWishlistPages();
    window.dispatchEvent(new CustomEvent('cr:wishlist:change', { detail: { items } }));
  };

  document.addEventListener('click', (event) => {
    const toggleButton = event.target.closest('[data-cr-wishlist-toggle]');
    if (toggleButton) {
      event.preventDefault();
      event.stopPropagation();
      toggle(toggleButton);
      return;
    }

    const removeButton = event.target.closest('[data-cr-wishlist-remove]');
    if (removeButton) {
      event.preventDefault();
      removeById(removeButton.dataset.crWishlistRemove);
    }
  });

  document.addEventListener('DOMContentLoaded', renderWishlistPages);
  document.addEventListener('shopify:section:load', renderWishlistPages);
  window.addEventListener('storage', (event) => {
    if (event.key === STORAGE_KEY) renderWishlistPages();
  });

  window.CardRepublicWishlist = {
    getItems: read,
    has,
    render: renderWishlistPages,
  };
})();
